/**
 * One crypto owner per device across tabs and windows.
 *
 * Every tab of one browser profile is the same device and shares one encrypted IndexedDB store. The crypto
 * library keeps the account (one-time keys) and the Olm sessions in memory and writes them back whole, so two
 * tabs running it at once overwrite each other: both top up one-time keys under the same key ids with different
 * keys, the server keeps one and the store the other, and every room key later sent with such a key is lost
 * (the message then stays "cannot be decrypted"). Big messengers allow one active instance per device for this
 * reason (Element: "open in another tab", WhatsApp Web: "Use here").
 *
 * Here the tab the person is using gets the crypto: a tab that comes to the front asks the owner (a
 * BroadcastChannel carries the ask, Web Locks keep it exclusive). An owner in the background hands over at once;
 * an owner in use answers "busy" and hands over as soon as it goes to the background. Only an owner that does not
 * answer at all (a frozen tab) loses the lock without a hand-over. A background owner nobody asks keeps it, so
 * notifications and decryption go on with the app in the background.
 */

export interface LockManagerLike {
  request(name: string, options: { ifAvailable?: boolean; steal?: boolean; signal?: AbortSignal }, callback: (lock: unknown) => Promise<void>): Promise<void>;
}
export interface ChannelLike {
  postMessage(data: unknown): void;
  addEventListener(type: 'message', fn: (e: { data: unknown }) => void): void;
  close(): void;
}
export interface TabLockEnv {
  locks: LockManagerLike | null;
  channel(name: string): ChannelLike | null;
  /** This tab is in front and focused (the person is using it). */
  inFront(): boolean;
  /** Calls fn whenever this tab comes to the front; returns the unsubscribe. */
  onFront(fn: () => void): () => void;
  /** Calls fn whenever this tab leaves the front (blur, hidden); returns the unsubscribe. */
  onBack(fn: () => void): () => void;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
}

/** How long a tab in front waits for any answer from the owner before taking the lock (a frozen owner never answers). */
export const STEAL_AFTER_MS = 6_000;

export function browserTabLockEnv(): TabLockEnv {
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManagerLike }) : undefined;
  const inFront = () => typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();
  const listen = (fn: () => void, when: () => boolean, events: [EventTarget, string][]) => {
    const handler = () => {
      if (when()) fn();
    };
    events.forEach(([t, e]) => t.addEventListener(e, handler));
    return () => events.forEach(([t, e]) => t.removeEventListener(e, handler));
  };
  return {
    locks: nav?.locks ?? null,
    channel: (name) => (typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel(name) as unknown as ChannelLike)),
    inFront,
    onFront: (fn) => (typeof window === 'undefined' ? () => undefined : listen(fn, inFront, [[window, 'focus'], [document, 'visibilitychange']])),
    onBack: (fn) => (typeof window === 'undefined' ? () => undefined : listen(fn, () => !inFront(), [[window, 'blur'], [document, 'visibilitychange']])),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  };
}

type Msg = { type: 'want'; from: string } | { type: 'busy'; to: string };

export class TabLock {
  private readonly id = Math.random().toString(36).slice(2);
  private channel: ChannelLike | null;
  private release: (() => void) | null = null;
  private waiting: Promise<void> | null = null;
  private stealTimer: unknown = null;
  private offFront: (() => void) | null = null;
  private offBack: (() => void) | null = null;
  private closed = false;
  private giveUp: (() => void) | null = null;
  /** Another tab asked while this owner was in use: hand over once it goes to the background. */
  private wanted = false;
  private yielding = false;
  /** Asked to hand over: finish what is running and stop using the crypto (then the lock is released). */
  onYield: (() => Promise<void>) | null = null;
  /** The lock was taken away without a hand-over (stolen from a frozen tab): stop using the crypto now. */
  onLost: (() => void) | null = null;

  constructor(
    private readonly name: string,
    private readonly env: TabLockEnv,
  ) {
    this.channel = env.channel(name);
    this.channel?.addEventListener('message', (e) => this.onMessage(e.data as Msg));
    this.offBack = env.onBack(() => {
      if (this.wanted) this.handOver();
    });
  }

  get held(): boolean {
    return this.release !== null || !this.env.locks;
  }

  /** Resolves once this tab owns the crypto. Without Web Locks (old browsers) it resolves at once, as before. */
  acquire(): Promise<void> {
    if (!this.env.locks || this.release || this.closed) return Promise.resolve();
    if (this.waiting) return this.waiting;
    const locks = this.env.locks;
    // Set once this wait is over (the grant may even come synchronously, before `waiting` is assigned below).
    let over = false;
    const waiting = new Promise<void>((resolve) => {
      let granted = false;
      this.giveUp = () => {
        granted = true;
        over = true;
        this.waiting = null;
        resolve();
      };
      const hold = (steal: boolean) =>
        locks
          .request(this.name, steal ? { steal: true } : {}, () => {
            // Already have it (a steal won first), or closed meanwhile: let it go at once.
            if ((granted && !steal) || this.closed) return Promise.resolve();
            granted = true;
            this.giveUp = null;
            this.stopAsking();
            return new Promise<void>((done) => {
              this.wanted = false;
              this.release = () => {
                this.release = null;
                done();
              };
              over = true;
              this.waiting = null;
              resolve();
            });
          })
          .catch(() => {
            // AbortError: another tab stole the lock from us while we held it.
            if (granted && this.release) {
              this.release = null;
              this.onLost?.();
            }
          });
      void hold(false);
      // While waiting: ask the owner whenever this tab comes to the front. An owner that is alive answers (hands
      // over or says it is busy); only when nothing answers is the lock taken.
      const ask = () => {
        if (granted || this.closed) return;
        this.channel?.postMessage({ type: 'want', from: this.id } satisfies Msg);
        if (this.stealTimer !== null) this.env.clearTimeout(this.stealTimer);
        this.stealTimer = this.env.setTimeout(() => {
          this.stealTimer = null;
          if (!granted && !this.closed && this.env.inFront()) void hold(true);
        }, STEAL_AFTER_MS);
      };
      this.onBusy = () => {
        if (this.stealTimer !== null) this.env.clearTimeout(this.stealTimer);
        this.stealTimer = null;
      };
      if (granted) return;
      this.offFront = this.env.onFront(ask);
      if (this.env.inFront()) ask();
    });
    if (!over) this.waiting = waiting;
    return waiting;
  }

  /** Lets the next tab have the crypto. */
  unlock(): void {
    this.release?.();
  }

  /** The tab is going away (sign-out): no more asking or handing over; a pending acquire() resolves (check `held`). */
  close(): void {
    this.closed = true;
    this.stopAsking();
    this.offBack?.();
    this.offBack = null;
    this.unlock();
    this.giveUp?.();
    this.giveUp = null;
    this.channel?.close();
    this.channel = null;
  }

  private onBusy: (() => void) | null = null;

  private stopAsking() {
    this.offFront?.();
    this.offFront = null;
    this.onBusy = null;
    if (this.stealTimer !== null) this.env.clearTimeout(this.stealTimer);
    this.stealTimer = null;
  }

  private handOver() {
    if (!this.release || this.yielding || !this.onYield) return;
    this.wanted = false;
    this.yielding = true;
    void this.onYield().finally(() => {
      this.yielding = false;
    });
  }

  private onMessage(msg: Msg) {
    if (!msg) return;
    if (msg.type === 'busy') {
      if (msg.to === this.id) this.onBusy?.();
      return;
    }
    if (msg.type !== 'want' || msg.from === this.id || !this.release) return;
    // The tab the person is using keeps it for now (no ping-pong between windows) and hands over once in the
    // background; it answers, so the asking tab knows it is alive and does not take the lock.
    if (this.env.inFront()) {
      this.wanted = true;
      this.channel?.postMessage({ type: 'busy', to: msg.from } satisfies Msg);
      return;
    }
    this.handOver();
  }
}
